import { useState, useEffect } from 'react';
import { useSearch } from '@tanstack/react-router';
import { 
    Save, ArrowLeft, Loader2, Sparkles, Zap, 
    FileCode, CheckCircle2, AlertTriangle, GitCommit, Activity
} from 'lucide-react';
import { Button, Card, Badge } from '@shared/ui';
import { Header } from '@widgets/header';
import { githubService, useProject, useQuickAnalysis } from '@shared/api';
import { sileo } from 'sileo';

export function EditorPage() {
    // Cast to any to bypass TanStack Router strict type checking for the build
    const search = useSearch({ from: '/editor' as any }) as any;
    const { projectId, filePath, type, branch } = search;
    
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [commitMessage, setCommitMessage] = useState(`fix: solve security issues in ${String(filePath).split('/').pop()}`);
    
    const { data: project } = useProject(projectId);
    const { mutate: analyze, isPending: isAnalyzing, data: analysisResult } = useQuickAnalysis();

    const repoFullName = project?.repository_url ? new URL(project.repository_url).pathname.substring(1).replace('.git', '') : null;

    useEffect(() => {
        const loadFile = async () => {
            setIsLoading(true);
            try {
                if (type === 'github' && repoFullName) {
                    const res = await githubService.getFile(repoFullName, filePath, branch);
                    setCode(res.content);
                } else {
                    // Local files aren't implemented for full text storage yet in this MVP
                    setCode("// Edita tu código aquí...");
                }
            } catch (err) {
                sileo.error({ title: 'Error loading file', description: 'Could not fetch content.' });
            } finally {
                setIsLoading(false);
            }
        };

        if (projectId && filePath) loadFile();
    }, [projectId, filePath, type, branch, repoFullName]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (type === 'github' && repoFullName) {
                await githubService.updateFile(repoFullName, {
                    path: filePath,
                    content: code,
                    message: commitMessage,
                    branch: branch
                });
                sileo.success({ title: 'Commit Successful', description: 'Changes pushed to GitHub.' });
            } else {
                sileo.success({ title: 'Saved Locally', description: 'Changes saved in browser storage.' });
            }
        } catch (err: any) {
            sileo.error({ title: 'Save Failed', description: err.message || 'Error performing action.' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleAnalyze = (isAI: boolean) => {
        analyze({
            project_id: projectId,
            snippets: [{
                content: code,
                language: String(filePath).endsWith('.cs') ? 'csharp' : String(filePath).endsWith('.py') ? 'python' : 'typescript',
                file_path: filePath
            }],
            analysis_types: isAI ? ['security', 'ai_logic'] : ['security']
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center p-20">
                    <Loader2 className="h-10 w-10 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col h-screen">
            <Header />
            
            {/* Toolbar */}
            <div className="bg-surface border-b border-border p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => window.close()} className="text-text-muted">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Exit
                    </Button>
                    <div className="h-8 w-[1px] bg-border mx-2" />
                    <div className="flex items-center gap-2">
                        <FileCode className="h-5 w-5 text-primary" />
                        <div>
                            <div className="text-xs font-mono text-text-primary font-bold">{filePath}</div>
                            <div className="text-[10px] text-text-muted font-mono uppercase tracking-widest">{type === 'github' ? `GitHub: ${branch}` : 'Local File'}</div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex gap-2 mr-4">
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleAnalyze(false)} 
                            className="text-emerald-500 hover:bg-emerald-500/10"
                            disabled={isAnalyzing}
                        >
                            <Zap className="h-4 w-4 mr-2" /> Linter
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleAnalyze(true)} 
                            className="text-cyan-400 hover:bg-cyan-400/10"
                            disabled={isAnalyzing}
                        >
                            <Sparkles className="h-4 w-4 mr-2" /> IA Pro
                        </Button>
                    </div>

                    {type === 'github' && (
                        <div className="flex items-center bg-background/50 rounded-md border border-border px-3 py-1.5 mr-2">
                            <GitCommit className="h-3.5 w-3.5 text-text-muted mr-2" />
                            <input 
                                value={commitMessage}
                                onChange={(e) => setCommitMessage(e.target.value)}
                                className="bg-transparent border-none text-xs font-mono text-text-primary focus:outline-none w-64"
                                placeholder="Commit message..."
                            />
                        </div>
                    )}

                    <Button 
                        variant="primary" 
                        onClick={handleSave} 
                        disabled={isSaving}
                        className={type === 'github' ? "bg-gradient-to-r from-primary to-blue-600" : "bg-emerald-500"}
                    >
                        {isSaving ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        {type === 'github' ? 'Commit Fix' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {/* Editor Area */}
            <div className="flex-1 flex overflow-hidden">
                <div className="flex-1 relative">
                    <textarea
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="w-full h-full p-8 bg-background font-mono text-sm text-text-primary resize-none focus:outline-none custom-scrollbar leading-relaxed"
                        spellCheck={false}
                    />
                </div>

                {/* Analysis Results Sidebar */}
                {(isAnalyzing || analysisResult) && (
                    <div className="w-96 border-l border-border bg-surface/50 overflow-y-auto p-4 space-y-4 animate-in slide-in-from-right">
                        <h3 className="font-bold font-mono text-xs uppercase tracking-widest text-text-muted mb-4 flex items-center gap-2">
                            <Activity className="h-3.5 w-3.5" />
                            Editor Analysis
                        </h3>

                        {isAnalyzing && (
                            <div className="py-20 text-center flex flex-col items-center gap-4">
                                <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />
                                <p className="text-[10px] font-mono text-text-muted uppercase tracking-widest">Escaneando cambios...</p>
                            </div>
                        )}

                        {analysisResult?.issues.map((issue, idx) => (
                            <Card key={idx} padding="sm" className={`border-l-4 ${issue.severity === 'critical' ? 'border-l-error' : 'border-l-warning'} bg-background/80`}>
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className={`h-4 w-4 mt-0.5 ${issue.severity === 'critical' ? 'text-error' : 'text-warning'}`} />
                                    <div className="min-w-0">
                                        <p className="text-xs font-bold text-text-primary truncate">{issue.title}</p>
                                        <p className="text-[10px] text-text-muted mt-1 leading-relaxed">{issue.description}</p>
                                        <Badge severity={issue.severity} size="sm" className="mt-2">Line {issue.line_start}</Badge>
                                    </div>
                                </div>
                            </Card>
                        ))}

                        {analysisResult && analysisResult.issues.length === 0 && (
                            <div className="p-8 text-center bg-success/5 border border-success/20 rounded-xl">
                                <CheckCircle2 className="h-8 w-8 text-success mx-auto mb-2 opacity-50" />
                                <p className="text-xs font-mono text-success uppercase font-bold">Código Securizado</p>
                                <p className="text-[10px] text-text-muted mt-1 italic">Tus cambios han resuelto las vulnerabilidades.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
